import api from './api';
import { ApiResponse } from '../types';
import { fetchAndSave } from '../utils/downloadResponse';

/**
 * Student Reports API client.
 *
 * Preview (JSON) uses `api.get`; the PDFs are drawn in the browser from it.
 * CSV downloads use `responseType: 'blob'` so axios doesn't JSON-parse them.
 */

export interface GradesheetSubjectHeader {
  subjectId:  string;
  name:       string;
  maxMarks:   number;
}

export interface GradesheetSubjectCell {
  subjectId:    string;
  display:      string;
  obtainedRaw:  number | null;
  status:       'PRESENT' | 'ABSENT' | 'PENDING' | 'NOT_TAKEN';
}

export interface GradesheetStudentRow {
  studentId:     string;
  name:          string;
  rollNumber:    string | number;
  sectionName:   string | null;
  perSubject:    GradesheetSubjectCell[];
  totalObtained: number;
  totalMax:      number;
  overallPct:    number;
  overallGrade:  string | null;
  /** Null when the student has nothing scored on this exam. */
  rank:          number | null;
  /** Pass/fail under the school's result rules; null when they're off. */
  outcome:       'PASS' | 'FAIL' | 'PENDING' | null;
  failedSubjectIds: string[];
}

export interface ExamGradesheet {
  exam:    { _id: string; name: string; startDate: string; endDate: string };
  session: { _id: string; name: string };
  class:   { _id: string; name: string };
  section: { _id: string; name: string } | null;
  subjects: GradesheetSubjectHeader[];
  students: GradesheetStudentRow[];
  stats: {
    totalStudents: number;
    ranked:        number;
    classAverage:  number;
    highest:       number;
    lowest:        number;
    /** Students per overall grade, highest first, then "Ungraded". */
    gradeDistribution: Array<{ grade: string; color: string; count: number }>;
    /** Whether the school has pass rules on; `passed`/`failed` are 0 when off. */
    passRules:     boolean;
    passed:        number;
    failed:        number;
  };
  generatedAt: string;
}

export interface ComparisonExamHeader {
  examId:    string;
  name:      string;
  startDate: string;
  endDate:   string;
}

export interface ComparisonStudentRow {
  studentId:   string;
  name:        string;
  rollNumber:  string | number;
  sectionName: string | null;
  perExam: Array<{
    examId:     string;
    overallPct: number | null;
    grade:      string | null;
  }>;
  averagePct:  number;
  deltaPct:    number | null;
  trend:       'up' | 'down' | 'flat' | 'na';
}

export interface ExamComparison {
  exams:   ComparisonExamHeader[];
  session: { _id: string; name: string };
  class:   { _id: string; name: string };
  section: { _id: string; name: string } | null;
  students: ComparisonStudentRow[];
  generatedAt: string;
}

// ── Query builders ──────────────────────────────────────────────────────────

const buildGradesheetParams = (input: {
  examId: string;
  sectionInstanceId?: string | null;
  format?: 'json' | 'csv';
}): string => {
  const sp = new URLSearchParams();
  sp.append('examId', input.examId);
  if (input.sectionInstanceId) sp.append('sectionInstanceId', input.sectionInstanceId);
  if (input.format) sp.append('format', input.format);
  return sp.toString();
};

const buildComparisonParams = (input: {
  examIds: string[];
  sectionInstanceId?: string | null;
  format?: 'json' | 'csv';
}): string => {
  const sp = new URLSearchParams();
  sp.append('examIds', input.examIds.join(','));
  if (input.sectionInstanceId) sp.append('sectionInstanceId', input.sectionInstanceId);
  if (input.format) sp.append('format', input.format);
  return sp.toString();
};

// ── JSON previews ───────────────────────────────────────────────────────────

const getGradesheet = async (input: {
  examId: string;
  sectionInstanceId?: string | null;
}): Promise<ExamGradesheet> => {
  const res = await api.get<ApiResponse<ExamGradesheet>>(
    `/reports/students/exam-gradesheet?${buildGradesheetParams({ ...input, format: 'json' })}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getComparison = async (input: {
  examIds: string[];
  sectionInstanceId?: string | null;
}): Promise<ExamComparison> => {
  const res = await api.get<ApiResponse<ExamComparison>>(
    `/reports/students/exam-comparison?${buildComparisonParams({ ...input, format: 'json' })}`
  );
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

// ── CSV downloads (the PDFs are drawn in the browser from the JSON above) ──

const downloadGradesheetCsv = (input: { examId: string; sectionInstanceId?: string | null }): Promise<void> =>
  fetchAndSave(
    () => api.get(`/reports/students/exam-gradesheet?${buildGradesheetParams({ ...input, format: 'csv' })}`, { responseType: 'blob' }),
    'gradesheet.csv',
  );

const downloadComparisonCsv = (input: { examIds: string[]; sectionInstanceId?: string | null }): Promise<void> =>
  fetchAndSave(
    () => api.get(`/reports/students/exam-comparison?${buildComparisonParams({ ...input, format: 'csv' })}`, { responseType: 'blob' }),
    'comparison.csv',
  );

const studentReportsService = {
  getGradesheet,
  getComparison,
  downloadGradesheetCsv,
  downloadComparisonCsv,
};

export default studentReportsService;
